import React from 'react';
import { useTranslation } from 'react-i18next';
import { IOccupancyStats, IRestaurant, IRestaurantDateStats, Label, Legend } from '@localina/core';
import { FoodBankIcon } from '@localina/icons';
import { isOverCapacity, RestaurantStatsChart } from './RestaurantStatsChart';
import { PathUtils } from '../../../../utils';
import { Path } from '../../../../enums';
import { useNavigate } from 'react-router-dom';
import { RestaurantStatsFigures } from '../RestaurantStatsFigures';
import { RestaurantStatsTimeAxis, RestaurantStatsTimeGrid } from '../RestaurantStatsTimeAxis';
import { ITimeRange } from '../RestaurantStatsTimeline';

interface IProps {
    stats: IRestaurantDateStats;
    range: ITimeRange;
    restaurant?: IRestaurant;
    previousWeekStats?: IOccupancyStats;
    // The per-area rows, drawn below the whole-day chart on the same time axis.
    children?: React.ReactNode;
}

const CHART_BELOW_AVERAGE_COLOR = '#709faf';
const CHART_ABOVE_AVERAGE_COLOR = '#15618a';
const CHART_OVER_CAPACITY_COLOR = '#8c2f2f';
const CHART_AVERAGE_LINE_COLOR = '#8a5a00';

export const RestaurantStatsAllDayTile: React.FC<IProps> = (props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const openReservations = () => {
        const date = props.stats.date;
        navigate(PathUtils.generateUrl(Path.RESTAURANT_RESERVATIONS, { restaurantId: props.restaurant?.id }, { date }));
    };

    // Over capacity only joins the legend on a day that has it, so red always means a real problem.
    const hasOverCapacity = [
        props.stats.occupancyStats,
        ...props.stats.areaOccupancyStats.flatMap((area) => area.shiftOccupancyStats).map((shift) => shift.stats),
    ].some((stats) => stats.list.some((it) => isOverCapacity(it.occupancy)));

    const legendItems = [
        { label: t('dashboard.chart.legend.belowAverage'), color: CHART_BELOW_AVERAGE_COLOR },
        { label: t('dashboard.chart.legend.aboveAverage'), color: CHART_ABOVE_AVERAGE_COLOR },
        ...(hasOverCapacity
            ? [{ label: t('dashboard.chart.legend.overCapacity'), color: CHART_OVER_CAPACITY_COLOR }]
            : []),
        { label: t('dashboard.chart.legend.averageLine'), color: CHART_AVERAGE_LINE_COLOR },
    ];

    return (
        <div className="restaurant-stats__tile">
            <div className="restaurant-stats__tile__header">
                <div className="restaurant-stats__tile__title">
                    <FoodBankIcon size="big" />
                    <Label type="title" value={t('dashboard.tile.title')} variant="h6" />
                </div>
                <Legend items={legendItems} />
            </div>
            <div className="restaurant-stats__tile__body">
                <div className="restaurant-stats__row">
                    <RestaurantStatsFigures
                        figures={props.stats.occupancyStats}
                        previousWeekFigures={props.previousWeekStats}
                    />
                    <div className="restaurant-stats__graph">
                        <div
                            className="restaurant-stats__tile__charts restaurant-stats__track"
                            onClick={openReservations}
                        >
                            <RestaurantStatsTimeGrid range={props.range} />
                            {props.stats.occupancyStats.list.length > 0 && (
                                <RestaurantStatsChart stats={props.stats.occupancyStats} range={props.range} />
                            )}
                        </div>
                        <RestaurantStatsTimeAxis range={props.range} />
                    </div>
                </div>
                {props.children}
            </div>
        </div>
    );
};
